import { CreditCard, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { SubscriptionConfig, SubscriptionTier, TierLimits } from '@/features/admin';
import { defaultTierLimits, defaultSubscriptionConfig } from '@/features/admin';

interface SubscriptionConfigTabProps {
  config: SubscriptionConfig;
  onChange: (config: SubscriptionConfig) => void;
  disabled?: boolean;
}

const TIER_ORDER: SubscriptionTier[] = ['free', 'silver', 'gold', 'platinum'];

const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: 'Free',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};

const TIER_COLORS: Record<SubscriptionTier, string> = {
  free: 'bg-gray-100 border-gray-300 dark:bg-gray-800',
  silver: 'bg-slate-100 border-slate-400 dark:bg-slate-800',
  gold: 'bg-amber-50 border-amber-400 dark:bg-amber-900/30',
  platinum: 'bg-purple-50 border-purple-400 dark:bg-purple-900/30',
};

const NUMERIC_FIELDS: { key: keyof TierLimits; label: string }[] = [
  { key: 'maxProperties', label: 'Max Properties' },
  { key: 'maxPhotosPerProperty', label: 'Photos per Property' },
  { key: 'maxDocumentsPerProperty', label: 'Documents per Property' },
];

export function SubscriptionConfigTab({ config, onChange, disabled }: SubscriptionConfigTabProps) {
  // Helper to update a specific tier
  const updateTier = (tier: SubscriptionTier, patch: Partial<TierLimits>) => {
    onChange({
      ...config,
      tiers: {
        ...config.tiers,
        [tier]: { ...config.tiers[tier], ...patch },
      },
    });
  };

  // Helper to reset a tier to defaults
  const resetTier = (tier: SubscriptionTier) => {
    updateTier(tier, defaultTierLimits[tier]);
  };

  // Helper to reset all tiers
  const resetAllTiers = () => {
    onChange(defaultSubscriptionConfig);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription Tiers
          </CardTitle>
          <CardDescription>Configure limits and features for each subscription tier</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-6">
            <div className="space-y-2">
              <Label>Default Tier</Label>
              <Select
                value={config.defaultTier}
                onValueChange={(v) => onChange({ ...config, defaultTier: v as SubscriptionTier })}
                disabled={disabled}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIER_ORDER.map((tier) => (
                    <SelectItem key={tier} value={tier}>
                      {TIER_LABELS[tier]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Grace Period (days)</Label>
              <Input
                type="number"
                min={0}
                className="w-32"
                value={config.gracePeriodDays}
                onChange={(e) => onChange({ ...config, gracePeriodDays: parseInt(e.target.value) || 0 })}
                disabled={disabled}
              />
            </div>

            <Button variant="outline" size="sm" onClick={resetAllTiers} disabled={disabled}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset All to Defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tier Cards Grid */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        {TIER_ORDER.map((tier) => (
          <Card key={tier} className={`border-2 ${TIER_COLORS[tier]}`}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle>{TIER_LABELS[tier]}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => resetTier(tier)}
                  disabled={disabled}
                  title="Reset tier"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {NUMERIC_FIELDS.map(({ key, label }) => {
                const value = config.tiers[tier][key];
                const isUnlimited = value === -1;

                return (
                  <div key={key} className="space-y-2">
                    <Label className="text-sm">{label}</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        className="h-9 flex-1"
                        value={isUnlimited ? '' : value}
                        onChange={(e) => {
                          const newValue = parseInt(e.target.value) || 0;
                          updateTier(tier, { [key]: newValue });
                        }}
                        disabled={disabled || isUnlimited}
                        placeholder={isUnlimited ? '∞' : ''}
                      />
                      <div className="flex items-center gap-1.5">
                        <Checkbox
                          id={`${tier}-${key}-unlimited`}
                          checked={isUnlimited}
                          onCheckedChange={(checked) => {
                            updateTier(tier, {
                              [key]: checked ? -1 : defaultTierLimits[tier][key],
                            });
                          }}
                          disabled={disabled}
                        />
                        <Label htmlFor={`${tier}-${key}-unlimited`} className="text-muted-foreground text-xs">
                          ∞
                        </Label>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
